import React, { useState } from "react";
import { Box, Button, Chip } from "@mui/material";
import { setupManagementUrl } from "../../../../config";
import CommissionForm from "./forms";
import DynamicTableForJson from "../../../../data/DynamicTable/dynamicTableForJson";
import Joyride from "react-joyride";
import Swal from "sweetalert2";
import { executeAnyMutation } from "../../../../data/DynamicServiceNew/jsondynamicservice";

const base_url = setupManagementUrl.uri;

// Status is a nullable int on the Commission entity:
//   null -> Pending, 0 -> Declined, 1 -> Approved, 2 -> Initiated
const COMMISSION_STATUS = {
  PENDING: null,
  DECLINED: 0,
  APPROVED: 1,
  INITIATED: 2,
};

const COMMISSION_STATUS_LABELS = {
  null: "Pending",
  0: "Declined",
  1: "Approved",
  2: "Initiated",
};

const COMMISSION_STATUS_COLORS = {
  null: "default",
  0: "error",
  1: "success",
  2: "warning",
};

// Normalizes a possibly-undefined/possibly-string status value to the
// int-keyed lookup above. Status comes back from the API as a real int
// (or null), so this mainly guards against undefined.
const getStatusKey = (status) => (status === undefined ? null : status);

const getStatusLabel = (status) =>
  COMMISSION_STATUS_LABELS[getStatusKey(status)] ?? COMMISSION_STATUS_LABELS[null];

const getStatusColor = (status) =>
  COMMISSION_STATUS_COLORS[getStatusKey(status)] ?? COMMISSION_STATUS_COLORS[null];
const safeFormatDate = (value) => {
  if (value == null || value === "" || value === "null" || value === "undefined")
    return "-";
  const d = new Date(value);
  return isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
};
// Columns reflect the actual Commission entity fields — see the
// createCommission response shape: Id, ProductId, CustomerId, LoanId,
// CommissionPercentage, CommissionAmount, IsActive, Status, CompanyId,
// RegionId, BranchId, InitiatedBy, InitiatedDate, ApprovedBy, ApprovedDate.
// There is no Name/Description/Amount/Percentage/CommissionType/
// EffectiveFrom/EffectiveTo on this entity.
const columns = [
  {
    field: "LoanId",
    headerName: "Loan ID",
    width: 120,
  },
  {
    field: "CustomerName",
    headerName: "Customer",
    width: 130,
  },
  {
    field: "ProductId",
    headerName: "Product ID",
    width: 120,
  },
  {
    field: "ReferralAgent" ,
    headerName: "Referral Agent",
    width: 120,
  },
  {
  field: "AmountToDisburse",
  headerName: "Principal",
  width: 120,
  valueFormatter: ({ value }) =>
  value == null
    ? ""
    : Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
  {
    field: "CommissionPercentage",
    headerName: "Commission %",
    width: 140,
    valueGetter: (params) =>
      params.row.CommissionPercentage != null
        ? `${params.row.CommissionPercentage}%`
        : "-",
  },
  {
    field: "CommissionAmount",
    headerName: "Commission Amount",
    width: 170,
    valueGetter: (params) =>
      params.row.CommissionAmount != null
        ? params.row.CommissionAmount.toLocaleString()
        : "-",
  },
  {
    field: "Status",
    headerName: "Status",
    width: 150,
    renderCell: (params) => (
      <Chip
        label={getStatusLabel(params.row.Status)}
        size="small"
        color={getStatusColor(params.row.Status)}
        sx={{ fontWeight: 600 }}
      />
    ),
  },
  {
    field: "InitiatedByName",
    headerName: "Initiated By",
    width: 150,
    valueGetter: (params) =>
      params.row.InitiatedByName || params.row.InitiatedBy || "-",
  },
  {
    field: "InitiatedDate",
    headerName: "Initiated Date",
    width: 180,
    renderCell: (params) => safeFormatDate(params.row.InitiatedDate),

  },
  {
    field: "ApprovedByName",
    headerName: "Approved By",
    width: 150,
    valueGetter: (params) =>
      params.row.ApprovedByName || params.row.ApprovedBy || "-",
  },
  {
    field: "ApprovedDate",
    headerName: "Approved Date",
    width: 180,
    renderCell: (params) => safeFormatDate(params.row.ApprovedDate),

  },
];

const approvedCommissionTourSteps = [
  {
    target: ".dynamic-table-header",
    content: "This is the Approved Commissions page where you can approve or reject initiated commissions.",
    placement: "top",
    disableBeacon: true,
  },
  {
    target: ".dynamic-table-search",
    content: "Use the search bar to find specific commissions.",
    placement: "bottom",
  },
  {
    target: ".dynamic-table-actions",
    content: "Select actions like Approve or Reject for each commission from this dropdown.",
    placement: "left",
  },
];

const ApprovedCommissionTable = () => {
  const [runTour, setRunTour] = useState(false);

  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem("decodedToken") || "{}");
    return user?.Id || null;
  };

  const handleApproveCommission = async (row, refetch) => {
    // Only allow approval if status is INITIATED (2)
    if (row.Status !== COMMISSION_STATUS.INITIATED) {
      await Swal.fire(
        "Cannot Approve",
        `This commission is already "${getStatusLabel(row.Status)}".`,
        "warning"
      );
      return;
    }

    const confirm = await Swal.fire({
      title: "Approve Commission?",
      text: `Approve commission for Loan #${row.LoanId}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#4caf50",
    });

    if (!confirm.isConfirmed) return;

    // Optional: Prompt for approval reason
    const { value: approvalReason } = await Swal.fire({
      title: "Approval Reason (Optional)",
      input: "textarea",
      inputPlaceholder: "Enter reason for approving this commission...",
      showCancelButton: true,
      confirmButtonText: "Submit",
      cancelButtonText: "Skip",
    });

    const userId = getUserId();
    const currentDate = new Date().toISOString();

    // FIX: payload now uses the real Commission entity field names instead
    // of the non-existent name/description/amount/percentage/commissionType/
    // effectiveFrom/effectiveTo.
    const response = await executeAnyMutation(
      "updateCommission",
      {
        id: row.Id,
        // Preserve existing fields using their real names
        productId: row.ProductId,
        customerId: row.CustomerId,
        loanId: row.LoanId,
        commissionPercentage: row.CommissionPercentage,
        commissionAmount: row.CommissionAmount,
        companyId: row.CompanyId,
        regionId: row.RegionId,
        branchId: row.BranchId,
        initiatedBy: row.InitiatedBy,
        initiatedDate: row.InitiatedDate,
        // initiateReason: row.InitiateReason || null,
        approvedBy: parseInt(userId),
        approvedDate: currentDate,
        // approvalReason: approvalReason || null,
        status: 1, // 1
        isActive: true,
      },
      "result",
      false,
      base_url
    );

    if (response?.results) {
      await Swal.fire("Approved!", "Commission has been approved successfully.", "success");
      if (refetch) refetch();
    } else {
      await Swal.fire("Error!", "Could not approve the commission. Please try again.", "error");
    }
  };

  const handleRejectCommission = async (row, refetch) => {
    // FIX: was comparing row.Status to the string "INITIATED"; Status is
    // an int, so this check never matched and rejection was effectively
    // always blocked (or allowed) regardless of actual status.
    if (row.Status !== COMMISSION_STATUS.INITIATED) {
      await Swal.fire(
        "Cannot Reject",
        `This commission is already "${getStatusLabel(row.Status)}".`,
        "warning"
      );
      return;
    }

    const confirm = await Swal.fire({
      title: "Reject Commission?",
      text: `Are you sure you want to reject the commission for Loan #${row.LoanId}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Reject",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#f44336",
    });

    if (!confirm.isConfirmed) return;

    const { value: rejectionReason } = await Swal.fire({
      title: "Rejection Reason (Required)",
      input: "textarea",
      inputPlaceholder: "Enter reason for rejecting this commission...",
      inputValidator: (value) => {
        if (!value) return "Please provide a reason for rejection!";
      },
      showCancelButton: true,
      confirmButtonText: "Submit",
    });

    if (!rejectionReason) return;

    const userId = getUserId();
    const currentDate = new Date().toISOString();

    // FIX: same field-name correction as approve — real Commission fields
    // instead of the non-existent name/description/amount/percentage/etc.
    const response = await executeAnyMutation(
      "updateCommission",
      {
        id: row.Id,
        // Preserve existing fields using their real names
        productId: row.ProductId,
        customerId: row.CustomerId,
        loanId: row.LoanId,
        commissionPercentage: row.CommissionPercentage,
        commissionAmount: row.CommissionAmount,
        companyId: row.CompanyId,
        regionId: row.RegionId,
        branchId: row.BranchId,
        // Preserve initiation fields
        initiatedBy: row.InitiatedBy,
        initiatedDate: row.InitiatedDate,
        // initiateReason: row.InitiateReason || null,
        // Rejection fields
        approvedBy: parseInt(userId),
        approvedDate: currentDate,
        // approvalReason: rejectionReason,
        status: 0, // 0
        isActive: false,
      },
      "result",
      false,
      base_url
    );

    if (response?.results) {
      await Swal.fire("Rejected!", "Commission has been rejected.", "success");
      if (refetch) refetch();
    } else {
      await Swal.fire("Error!", "Could not reject the commission. Please try again.", "error");
    }
  };

  const actions = {
    entity: localStorage.getItem("SelectedRight"),
    edit: {
      key: "EDIT",
      button_name: "Edit",
      Show_Button: false,
      permission: "EditPermission",
    },
    approve: {
      key: "APPROVE",
      button_name: "Approve",
      Show_Button: true,
      permission: "EditPermission",
      onClick: (row, refetch) => handleApproveCommission(row, refetch),
    },
    reject: {
      key: "REJECT",
      button_name: "Reject",
      Show_Button: true,
      permission: "EditPermission",
      onClick: (row, refetch) => handleRejectCommission(row, refetch),
    },
    dataObject: {
      ignoreFields: ["UpdateDate", "CreateDate"],
      objectName: "allCommissions",
      parameters: null,
    },
    required: "rightIds",
    showStatus: true,
    showMore: {
      search: false,
      min: 0,
      max: 0,
    },
  };

  return (
    <Box m="20px">
      <Joyride
        steps={approvedCommissionTourSteps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        styles={{
          options: {
            primaryColor: "#1976d2",
            zIndex: 10000,
          },
        }}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button onClick={() => setRunTour(true)} variant="outlined" color="primary">
          Start Page Tour
        </Button>
      </Box>

      <DynamicTableForJson
        title="Approved Commissions Management"
        subtitle="Approve or Reject Initiated Commissions"
        FormComponent={CommissionForm}
        columns={columns}
        base_url={base_url}
        actions={actions}
        tourSteps={approvedCommissionTourSteps}
      />
    </Box>
  );
};

export default ApprovedCommissionTable;